import React, { useEffect, useState, useCallback } from 'react'
import { useI18n } from '@ggs/i18n'
import { saveEntityOperation, useSelector } from '@ggs/store'
import { addNotification, useDispatch } from '@ggs/store'
import { AddProjectCustomProductModal } from './AddProjectCustomProductModal'
import { get } from 'lodash'
import { useDrupalApi } from '../../../local/hooks'
import { getViewedPageParams } from '@ggs/utils'
import { useUrlParams } from '@ggs/hooks'

/**
 * @typedef {import('../../../definitions').CustomProductMedia} CustomProductMedia
 * @typedef {import('../../../definitions').MimeeqCustomProduct} MimeeqCustomProduct
 */

/**
 * Add project custom product action listener for mimeeqapi.
 * @return {JSX.Element}
 */
const AddProjectCustomProductAction = () => {
  const { t } = useI18n()
  const dispatch = useDispatch()
  const userId = get(window || {}, 'drupalSettings.user.uid', 1)
  const entityOperation = useSelector((state) => state.settings.entityOperation || {})
  const { createCustomProductMedia } = useDrupalApi()
  const { removeQueryParam } = useUrlParams()

  /**
   * After login refresh utility to recover the payload data from Redux correlating by product short code.
   * @return {MimeeqCustomProduct}
   */
  const resolveCurrentEntityPayload = () => {
    const pageParams = getViewedPageParams()
    const shortCode = get(pageParams, 'short_code', null)
    if (shortCode) {
      const operation = get(entityOperation, `custom_product_${shortCode}`, null)
      if (operation?.attributes) {
        return operation.attributes
      }
    }
  }

  /**
   * @type {CustomProductMedia}
   */
  const defaultCustomProduct = null
  const [customProductMedia, setCustomProductMedia] = useState(defaultCustomProduct)
  const [clickedWishlist, setClickedWishlist] = useState(null)

  /**
   * @param {MimeeqCustomProduct} eventPayload
   */
  const createCustomProduct = async (eventPayload) => {
    let currentUrl = window?.location?.pathname + window?.location?.search
    const pageParams = getViewedPageParams()
    const shortCodeQueryValue = get(pageParams, 'short_code', null)
    if (shortCodeQueryValue) {
      // Remove only the "short_code={code}" parameter from the URL
      currentUrl = removeQueryParam(currentUrl, 'short_code', shortCodeQueryValue)
    }

    const imageBase64 = (eventPayload?.image || '').replace(/data:image\/.*base64,/gi, '')
    const mediaEntity = await createCustomProductMedia({
      label: eventPayload?.productName || t('Unknown Product Name'),
      image: imageBase64,
      url: `${currentUrl}&shortCode=${eventPayload?.shortCode}` || '',
    })

    if (mediaEntity) {
      setCustomProductMedia(mediaEntity)
      setClickedWishlist(true)
    }
  }

  /**
   * Add project custom product action listener for mimeeqapi.
   * @param {any} event
   */
  const addToCartHandler = useCallback(async (event) => {
    /**
     * @type {MimeeqCustomProduct}
     */
    const eventPayload = event?.detail
    const embedShortCode = eventPayload?.embedShortCode
    if (!embedShortCode) {
      dispatch(
        addNotification(
          'product_id_not_found',
          'error',
          t('Product id not received in add product event payload, please try again.')
        )
      )

      return
    }

    if (!userId) {
      dispatch(saveEntityOperation(`custom_product_${embedShortCode}`, 'add_to_project', eventPayload))
    } else {
      await createCustomProduct(eventPayload)
    }
  }, [])

  useEffect(() => {
    document.addEventListener('mimeeq-add-to-cart', addToCartHandler)
    return () => {
      document.removeEventListener('mimeeq-add-to-cart', addToCartHandler)
    }
  }, [])

  // Resume add to project action, creating the media custom product into Drupal.
  useEffect(() => {
    const fetchData = async () => {
      // Check if the URL contains the "short_code={shortCode}" query parameter
      const pageParams = getViewedPageParams()
      const shortCodeArg = get(pageParams, 'short_code', null)
      if (userId !== 0 && shortCodeArg) {
        const entityOperationPayload = resolveCurrentEntityPayload()
        if (entityOperationPayload?.productName) {
          await createCustomProduct(entityOperationPayload)
        }
      }
    }
    fetchData() // Invoke the async function immediately
  }, [])

  /**
   * Callback to change clickedWishlistState to false.
   */
  const updateClickedWishlistState = () => {
    setClickedWishlist(false)
  }

  return (
    <>
      {(customProductMedia && clickedWishlist !== null) && (
        <AddProjectCustomProductModal
          resourceType="media--custom_product"
          resourceId={get(customProductMedia, 'mid[0].value', null)}
          updateClickedWishlistState={updateClickedWishlistState}
          clickedWishlist={clickedWishlist}
        />
      )}
    </>
  )
}

export default AddProjectCustomProductAction
